import de from 'date-fns/locale/de';
import en from 'date-fns/locale/en-US';
import fr from 'date-fns/locale/fr';
import it from 'date-fns/locale/it';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import ReactDatepicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { Datetime, EDateFormat, ELanguageTags, ESize } from '@core/type';
import { convertDateToDatetime, convertDatetimeToDate } from '@core/util';

import { Box } from '../layout';
import { DatepickerGlobalStyle } from './DatepickerGlobalStyle';
import { DatepickerInput } from './DatepickerInput';
import { DateFormatByLocaleProps, DatepickerProps } from './interface-datepicker';

const YEAR_ITEMS = 8;

const dateFormatByLocale: DateFormatByLocaleProps = {
  [ELanguageTags.EN_US]: {
    [EDateFormat.ddMMyyyy]: 'MMM dd, yyyy',
    [EDateFormat.ddMM]: 'MMM dd',
    [EDateFormat.yMd]: 'dd/MM/yyyy',
    [EDateFormat.ddMMyyyyhhmm]: 'MMM dd, yyyy, hh:mm aa',
  },
  [ELanguageTags.DE_DE]: {
    [EDateFormat.ddMMyyyy]: 'dd. MMM yyyy',
    [EDateFormat.ddMM]: 'dd. MMM',
    [EDateFormat.yMd]: 'dd/MM/yyyy',
    [EDateFormat.ddMMyyyyhhmm]: 'dd. MMM yyyy, HH:mm',
  },
  [ELanguageTags.IT_IT]: {
    [EDateFormat.ddMMyyyy]: 'dd MMM yyyy',
    [EDateFormat.ddMM]: 'dd MMM',
    [EDateFormat.yMd]: 'dd/MM/yyyy',
    [EDateFormat.ddMMyyyyhhmm]: 'dd MMM yyyy, HH:mm',
  },
  [ELanguageTags.FR_FR]: {
    [EDateFormat.ddMMyyyy]: 'dd MMM yyyy',
    [EDateFormat.ddMM]: 'dd MMM',
    [EDateFormat.yMd]: 'dd/MM/yyyy',
    [EDateFormat.ddMMyyyyhhmm]: 'dd MMM yyyy, HH:mm',
  },
  [ELanguageTags.DEFAULT]: {
    [EDateFormat.ddMMyyyy]: 'MMM dd, yyyy',
    [EDateFormat.ddMM]: 'MMM dd',
    [EDateFormat.yMd]: 'dd/MM/yyyy',
    [EDateFormat.ddMMyyyyhhmm]: 'MMM dd, yyyy, hh:mm aa',
  },
};

registerLocale(ELanguageTags.DE_DE, de);
registerLocale(ELanguageTags.EN_US, en);
registerLocale(ELanguageTags.FR_FR, fr);
registerLocale(ELanguageTags.IT_IT, it);
registerLocale(ELanguageTags.DEFAULT, en);

export const Datepicker = ({
  id,
  language,
  onChange,
  dateFormat,
  includeDates: includeDatesProp,
  excludeDates: excludedDatesProp,
  excludeDateIntervals: excludeDateIntervalsProp,
  placeholder,
  size = ESize.MD,
  value: valueProp,
  minDate,
  maxDate,
  field,
  fieldState,
  isSubmitted,
  ...restBoxProps
}: DatepickerProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const format: string = dateFormatByLocale[language][dateFormat || EDateFormat.yMd];
  const value: Datetime = field?.value || valueProp;

  const handleOnChange = useCallback(
    (date: Date, event?: SyntheticEvent<'input', Event>) => {
      onChange(convertDateToDatetime(date), event);
    },
    [onChange],
  );

  const includeDates = useMemo(
    () => includeDatesProp?.map((date) => convertDatetimeToDate(date)),
    [includeDatesProp],
  );

  const excludeDates = useMemo(
    () => excludedDatesProp?.map((date) => convertDatetimeToDate(date)),
    [excludedDatesProp],
  );

  const excludeDateIntervals = useMemo(
    () =>
      excludeDateIntervalsProp?.map(({ start, end }) => ({
        start: convertDatetimeToDate(start),
        end: convertDatetimeToDate(end),
      })),
    [excludeDateIntervalsProp],
  );

  const handleCalendarClose = () => setIsOpen(false);
  const handleCalendarOpen = () => setIsOpen(true);

  return (
    <Box {...restBoxProps}>
      <DatepickerGlobalStyle />
      <ReactDatepicker
        locale={language}
        dateFormat={format}
        selected={value && convertDatetimeToDate(value)}
        minDate={minDate && convertDatetimeToDate(minDate)}
        maxDate={maxDate && convertDatetimeToDate(maxDate)}
        customInput={
          <DatepickerInput
            id={id}
            size={size}
            fieldState={fieldState}
            isOpen={isOpen}
            isSubmitted={isSubmitted}
            placeholder={placeholder}
          />
        }
        onChange={handleOnChange}
        showMonthDropdown
        showYearDropdown
        scrollableYearDropdown
        disabledKeyboardNavigation
        yearDropdownItemNumber={YEAR_ITEMS}
        excludeDates={excludeDates}
        excludeDateIntervals={excludeDateIntervals}
        includeDates={includeDates}
        placeholderText={placeholder}
        onCalendarClose={handleCalendarClose}
        onCalendarOpen={handleCalendarOpen}
      />
    </Box>
  );
};
